import React, { useState, useEffect } from 'react';
import { ToolResultProps } from '../types';
import { getDocumentsInfo, extractDocumentNames, generateDocumentUrl, DocumentInfo } from './documentHelpers';
import { Button } from "../../../../../../../components/ui/button";
import { Skeleton } from "../../../../../../../components/ui/skeleton";
import { LayoutGrid, ChevronRight } from "lucide-react";
import FileIconSVG from "@/taskpane/shared/components/FileIconSVG";
import { ICON_SIZES } from "@/lib/icon-sizes";

const ListDocumentsResult: React.FC<ToolResultProps> = ({
  thought,
  onDocumentClick
}) => {
  const { toolResult, content } = thought;
  const metadata = toolResult?.metadata || thought.metadata || {};
  const documentIds = metadata.document_ids || [];
  const totalItems = metadata.total_items || documentIds.length;

  const [documentsInfo, setDocumentsInfo] = useState<DocumentInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [urlsMap, setUrlsMap] = useState<Map<string, string | null>>(new Map());
  const [showAll, setShowAll] = useState(false);
  const INITIAL_DISPLAY_COUNT = 4;

  const extractedNames = extractDocumentNames(content || toolResult?.content);

  useEffect(() => {
    const loadDocumentsInfo = async () => {
      if (documentIds.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const info = await getDocumentsInfo(documentIds);
        const improvedInfo = info.map((doc, index) => {
          const isFallbackName = doc.name === doc.id || (doc.name.includes('Document') && doc.name.includes('...'));
          if (isFallbackName && extractedNames[index]) return { ...doc, name: extractedNames[index].trim() };
          return doc;
        });
        
        setDocumentsInfo(improvedInfo);
        
        const urlsPromises = improvedInfo.map(async (doc) => {
          if (doc.source_path) return { id: doc.id, url: await generateDocumentUrl(doc.source_path) };
          return { id: doc.id, url: doc.pdf_url };
        });
        
        const urlsResults = await Promise.all(urlsPromises);
        const newUrlsMap = new Map<string, string | null>();
        urlsResults.forEach(({ id, url }) => newUrlsMap.set(id, url));
        setUrlsMap(newUrlsMap);
      } catch (err) {
        setDocumentsInfo(documentIds.map((id, index) => ({ id, name: extractedNames[index] || `Doc ${index + 1}`, pdf_url: null })));
      } finally {
        setIsLoading(false);
      }
    };
    loadDocumentsInfo();
  }, [documentIds.join(',')]);

  if (isLoading && documentIds.length > 0) {
    return (
      <div className="mt-1 grid grid-cols-2 gap-1">
        <Skeleton className="h-7 w-full rounded" />
        <Skeleton className="h-7 w-full rounded" />
      </div>
    );
  }

  const displayDocuments = documentsInfo.length > 0 ? documentsInfo : documentIds.map((id, index) => ({ id, name: extractedNames[index] || `Doc ${index + 1}`, pdf_url: null }));
  if (displayDocuments.length === 0) return null;

  const visibleDocuments = showAll ? displayDocuments : displayDocuments.slice(0, INITIAL_DISPLAY_COUNT);
  const remainingCount = displayDocuments.length - INITIAL_DISPLAY_COUNT;

  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-center justify-between px-1">
        {remainingCount > 0 && (
          <Button variant="link" size="sm" className="h-4 p-0 text-[10px] text-blue-500 hover:text-blue-600" onClick={() => setShowAll(true)}>
            +{remainingCount} more
          </Button>
        )}
        {showAll && displayDocuments.length > INITIAL_DISPLAY_COUNT && (
          <Button variant="link" size="sm" className="h-4 p-0 text-[10px] text-muted-foreground" onClick={() => setShowAll(false)}>
            Show less
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1">
        {visibleDocuments.map((doc) => {
          const url = urlsMap.get(doc.id) ?? doc.pdf_url;
          return (
            <Button
              key={doc.id}
              variant="ghost"
              size="sm"
              disabled={!url}
              className="justify-start h-7 py-0 px-2 hover:bg-muted/50 transition-colors group border border-transparent hover:border-border/20 rounded text-left overflow-hidden"
              onClick={() => onDocumentClick?.(doc.id, url, doc.name)}
            >
              <div className="flex items-center gap-1.5 w-full">
                <FileIconSVG filename={doc.name} size={ICON_SIZES.sm} className="opacity-80" />
                <span className="text-[10px] truncate flex-1 text-foreground/80">{doc.name}</span>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default ListDocumentsResult;
